import type { AvlAlias } from '@/services/approvedVendorList';
import { PalmettoButton, PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback } from 'react';

interface AliasEditorProps {
  value: AvlAlias[];
  onChange: (next: AvlAlias[]) => void;
  disabled?: boolean;
  /**
   * Server-side per-equipmentType uniqueness error to surface inline. Also carries the
   * "omnidian" source rejection (`assertNoOmnidianAliasSource`) — `source` here is free text with
   * no client-side enum, so a user typing "omnidian" is only caught on submit, not as they type.
   */
  errorMessage?: string;
}

const emptyAlias = (): AvlAlias => ({ source: '', value: '' });

const addIcon = <PalmettoIcon name="add" size="sm" />;

/**
 * Aliases are edited as a two-column multi-row list (source optional, value required).
 * Trimming, empty-row dropping, and case-insensitive de-dupe happen on form submit, not here,
 * so the editor stays a faithful mirror of the raw rows the user typed.
 */
export const AliasEditor = ({ value, onChange, disabled, errorMessage }: AliasEditorProps) => {
  const rows = value.length > 0 ? value : [emptyAlias()];

  const handleValueChange = useCallback(
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = rows.map((row, i) => (i === index ? { ...row, value: e.target.value } : row));
      onChange(next);
    },
    [rows, onChange],
  );

  const handleSourceChange = useCallback(
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = rows.map((row, i) => (i === index ? { ...row, source: e.target.value } : row));
      onChange(next);
    },
    [rows, onChange],
  );

  const handleAdd = useCallback(() => {
    onChange([...rows, emptyAlias()]);
  }, [rows, onChange]);

  const handleRemove = useCallback(
    (index: number) => () => {
      const next = rows.filter((_, i) => i !== index);
      onChange(next.length > 0 ? next : [emptyAlias()]);
    },
    [rows, onChange],
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="body-bold-300 text-text-primary">Aliases</div>
      {rows.map((alias, index) => (
        <div key={index} className="flex items-end gap-8">
          <div className="w-36">
            <PalmettoTextInput
              id={`alias-source-${index}`}
              label="Source"
              placeholder="Source (optional)"
              value={alias.source}
              onChange={handleSourceChange(index)}
              disabled={disabled}
            />
          </div>
          <div className="flex-auto">
            <PalmettoTextInput
              id={`alias-value-${index}`}
              label="Value"
              placeholder="Alias used for equipment matching"
              value={alias.value}
              onChange={handleValueChange(index)}
              disabled={disabled}
            />
          </div>
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            aria-label={`Remove alias ${index + 1}`}
            onClick={handleRemove(index)}
            disabled={disabled}
          >
            <PalmettoIcon name="trash" size="sm" />
          </PalmettoButton>
        </div>
      ))}
      <div>
        <PalmettoButton
          type="button"
          emphasis="secondary"
          layout="hug"
          leadingIcon={addIcon}
          onClick={handleAdd}
          disabled={disabled}
        >
          Add Alias
        </PalmettoButton>
      </div>
      {errorMessage && (
        <div role="alert" className="body-200 text-text-error">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

/**
 * Normalize alias rows for submission: trim values, drop rows with empty values,
 * de-dupe case-insensitively on value (first occurrence wins; non-blank source takes
 * priority over blank source for the same value).
 */
export const normalizeAliases = (rows: AvlAlias[]): AvlAlias[] => {
  const seen = new Map<string, AvlAlias>();
  for (const row of rows) {
    const trimmedValue = row.value.trim();
    if (!trimmedValue) continue;
    const key = trimmedValue.toLowerCase();
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, { source: row.source.trim(), value: trimmedValue });
    } else if (!existing.source && row.source.trim()) {
      seen.set(key, { source: row.source.trim(), value: existing.value });
    }
  }
  return Array.from(seen.values());
};
